import React, { useEffect, useState } from 'react';

/**
 * Outer starburst silhouette only (matches iOS `StarburstOutline` / DesignSystem path).
 * viewBox 0 0 1450 1390
 */
const STARBURST_OUTER_PATH_D =
  'M500.578 3.283C516.416-1.83 532.253-.971 548.091 5.862L724.565 80.524L901.041 5.862C916.878-.925 932.716-1.785 948.554 3.283C964.391 8.351 976.835 18.261 985.885 33.013L1084.3 199.305L1270.96 240.031C1287.93 243.425 1301.5 252.203 1311.68 266.366C1321.87 280.529 1325.82 296.073 1323.56 312.997L1304.9 504.743L1432.16 650.675C1443.47 663.118 1449.13 677.825 1449.13 694.794C1449.13 711.762 1443.47 726.468 1432.16 738.912L1304.9 884.844L1323.56 1076.59C1325.82 1093.56 1321.87 1109.12 1311.68 1123.29C1301.5 1137.45 1287.93 1146.21 1270.96 1149.56L1084.3 1190.28L985.885 1356.57C976.835 1371.28 964.391 1381.19 948.554 1386.3C932.716 1391.42 916.878 1390.56 901.041 1383.72L724.565 1309.06L548.091 1383.72C532.253 1390.51 516.416 1391.37 500.578 1386.3C484.741 1381.24 472.297 1371.33 463.247 1356.57L364.828 1190.28L178.172 1149.56C161.203 1146.16 147.629 1137.41 137.447 1123.29C127.266 1109.17 123.306 1093.6 125.568 1076.59L144.234 884.844L16.969 738.912C5.656 726.468 0 711.762 0 694.794C0 677.825 5.656 663.118 16.969 650.675L144.234 504.743L125.568 312.997C123.306 296.028 127.266 280.462 137.447 266.299C147.628 252.135 161.203 243.38 178.172 240.031L364.828 199.305L463.247 33.013C472.297 18.306 484.741 8.396 500.578 3.283Z';

const ROTATE_MS = 380;
const PULSE_LEG_MS = 550;
const ROTATION_STEP_DEG = 40;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Default loading indicator — matches native `HomeDefaultLoadingIndicator`:
 * solid starburst outline, rotate step then scale/opacity pulse; repeat.
 */
const DefaultLoadingIndicator = ({ dots = true, darkMode = false }) => {
  const [rotationDeg, setRotationDeg] = useState(0);
  const [pulseCompressed, setPulseCompressed] = useState(false);

  useEffect(() => {
    if (!dots) return undefined;

    let cancelled = false;

    const loop = async () => {
      while (!cancelled) {
        setRotationDeg((r) => r + ROTATION_STEP_DEG);
        await sleep(ROTATE_MS);
        if (cancelled) break;

        setPulseCompressed(true);
        await sleep(PULSE_LEG_MS);
        if (cancelled) break;

        setPulseCompressed(false);
        await sleep(PULSE_LEG_MS);
      }
    };

    loop();

    return () => {
      cancelled = true;
    };
  }, [dots]);

  if (!dots) return null;

  return (
    <div className="flex pt-1.5 max-[1200px]:pt-1 max-[900px]:pt-0.5 [@media(max-height:800px)]:pt-1 [@media(max-height:700px)]:pt-0.5">
      <div
        className="flex h-3.5 w-3.5 items-center justify-center max-[1200px]:h-3 max-[1200px]:w-3 max-[900px]:h-2.5 max-[900px]:w-2.5 [@media(max-height:800px)]:h-3 [@media(max-height:800px)]:w-3 [@media(max-height:700px)]:h-2.5 [@media(max-height:700px)]:w-2.5"
        style={{
          transform: `rotate(${rotationDeg}deg)`,
          transition: `transform ${ROTATE_MS}ms ease-in-out`,
        }}
      >
        <div
          className="flex h-full w-full items-center justify-center"
          style={{
            transform: pulseCompressed ? 'scale(0.82)' : 'scale(1)',
            opacity: pulseCompressed ? 0.45 : 1,
            transition: `transform ${PULSE_LEG_MS}ms linear, opacity ${PULSE_LEG_MS}ms linear`,
          }}
        >
          <svg
            viewBox="0 0 1450 1390"
            className={`h-full w-full ${darkMode ? 'text-white' : 'text-black'}`}
            aria-hidden
            focusable="false"
          >
            <path d={STARBURST_OUTER_PATH_D} fill="currentColor" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default DefaultLoadingIndicator;
